import { PayloadUploaded } from '@/components/define-document-write/define-document-write.type';
import { FileTypeLabelEnum } from '@/constant';
import { TDocumentUploadDefineItem } from '@/services';
import { TDefineDocumentUploadListStore } from '@/stores/define-document-upload';
import { randomId } from '@/utils';
import { useCallback } from 'react';

import { StatusDocumentUpload } from '../define-document-upload.config';

interface UseDocumentUploadProps {
	mergeListDocument: TDefineDocumentUploadListStore;
	createHiddenDocument: (
		document: TDefineDocumentUploadListStore[0],
		keepMapping?: boolean
	) => TDefineDocumentUploadListStore[0];
	updateDocumentList: (
		document: TDefineDocumentUploadListStore[0]
	) => TDefineDocumentUploadListStore;
	updateDocumentChange: (
		oldDoc: TDefineDocumentUploadListStore[0],
		newDoc: TDefineDocumentUploadListStore[0]
	) => void;
	setListDocumentChange: (documents: TDefineDocumentUploadListStore) => void;
}

export const useDocumentUpload = ({
	mergeListDocument,
	createHiddenDocument,
	updateDocumentList,
	updateDocumentChange,
	setListDocumentChange,
}: UseDocumentUploadProps) => {
	const handleEditDocument = useCallback(
		(data: PayloadUploaded) => {
			const { rowId, fileUploaded } = data;
			const documentToDelete = mergeListDocument.find(
				(doc) => doc.id === rowId
			);
			if (!documentToDelete) return;

			const hiddenDocument = createHiddenDocument(documentToDelete, false);
			const updatedDocuments = updateDocumentList(hiddenDocument);

			const newDocument = {
				...hiddenDocument,
				mappingList: documentToDelete.mappingList?.filter(
					(item) =>
						item.refTableName !== documentToDelete.fileList.refTableName ||
						item.refTableId !== documentToDelete.fileList.refTableId
				),
				id: randomId(),
				fileName: fileUploaded?.name || '',
				fileList: { ...fileUploaded } as TDocumentUploadDefineItem,
				typeName: FileTypeLabelEnum.FORM,
				statusDocumentUpload: StatusDocumentUpload.ADD,
				hidden: false,
			};

			const hiddenDocumentIndex = updatedDocuments.findIndex(
				(doc) => doc.id === hiddenDocument.id
			);
			updatedDocuments.splice(hiddenDocumentIndex + 1, 0, newDocument);

			setListDocumentChange(updatedDocuments);
		},
		[
			mergeListDocument,
			createHiddenDocument,
			updateDocumentList,
			setListDocumentChange,
		]
	);

	const handleCreateDocument = useCallback(
		(data: PayloadUploaded) => {
			const { rowId, fileUploaded } = data;
			const selectedDocument = mergeListDocument.find(
				(doc) => doc.id === rowId
			);

			if (selectedDocument) {
				const newDocument = {
					...selectedDocument,
					fileName: fileUploaded?.name || '',
					fileList: { ...fileUploaded } as TDocumentUploadDefineItem,
					typeName: FileTypeLabelEnum.FORM,
				};

				updateDocumentChange(selectedDocument, newDocument);
			}
		},
		[mergeListDocument, updateDocumentChange]
	);

	const onUploaded = useCallback(
		(data: PayloadUploaded) => {
			const { mode } = data;
			if (mode === 'UPDATE') {
				handleEditDocument(data);
				return;
			}
			if (mode === 'CREATE') {
				handleCreateDocument(data);
				return;
			}
		},
		[handleEditDocument, handleCreateDocument]
	);

	return {
		onUploaded,
	};
};
